import type { Mesh, MeshCallbacks, MeshMessageBodyType, MeshMessageType } from '../mesh.ts';
import type { DomainInfo } from '../state/types/domain-registry.ts';

import type { MeshAction, MeshChannel, MeshEvent, MeshSubscriber } from '../../types/mesh/api';

class MeshCallbacksAdapter {
    readonly #callbacks?: MeshCallbacks;

    constructor(callbacks?: MeshCallbacks) {
        this.#callbacks = callbacks;
    }

    subscriber(): MeshSubscriber<MeshMessageBodyType> {
        return (event: MeshEvent<MeshMessageBodyType>, node, _mesh) => {
            switch (event.type) {
                case 'member-added':
                    this.#callbacks?.onMemberAdded?.(node, event.node);
                    break;
                case 'member-removed':
                    this.#callbacks?.onMemberRemoved?.(node, event.node);
                    break;
                case 'message-received':
                    this.#callbacks?.onMessage?.(node, event.message);
                    break;
            }
        };
    }
}

class ChannelMesh implements Mesh {

    readonly #mesh: MeshChannel;

    constructor(mesh: MeshChannel) {
        this.#mesh = mesh;
    }

    close() {
        // no-op
    }

    register(nodeId: string | undefined, _domains: Array<DomainInfo>, callbacks?: MeshCallbacks): string {
        const adapter = new MeshCallbacksAdapter(callbacks);
        return this.#mesh.subscribe(nodeId, adapter.subscriber());
    }

    unregister(nodeId: string) {
        this.#mesh.unsubscribe(nodeId);
    }

    publish(nodeId: string, message: MeshMessageType) {
        const action = {
            type: 'publish-message',
            message: message
        } as MeshAction<MeshMessageBodyType>;
        this.#mesh.execute(nodeId, action);
    }

    addUsers(nodeId: string, added: readonly string[]) {
        this.#mesh.execute(nodeId, { type: 'add-users', added });
    }

    removeUsers(nodeId: string, removed: readonly string[]) {
        this.#mesh.execute(nodeId, { type: 'remove-users', removed });
    }
}

export function channelCluster(channel: MeshChannel): Mesh {
    return new ChannelMesh(channel);
}
